import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../hooks/useAuth';
import { useSendContactMessage } from '../hooks/useContact';

const CONTACT_EMAIL = 'info@coffee214.com';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function ContactPage() {
  const { data: user } = useAuth();
  const sendMessage = useSendContactMessage();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  // Пастка для ботів — людина її не бачить і не заповнює.
  const [website, setWebsite] = useState('');
  const [attempted, setAttempted] = useState(false);

  // Залогіненому підставляємо імʼя та email (їх можна змінити) — лише поки
  // поля ще порожні, щоб не перетерти вже введене.
  useEffect(() => {
    if (!user) return;
    setName((current) => current || user.name || '');
    setEmail((current) => current || user.email);
  }, [user]);

  const errors = {
    name: !name.trim(),
    email: !EMAIL_PATTERN.test(email.trim()),
    subject: !subject.trim(),
    message: !message.trim(),
  };
  const isValid = !Object.values(errors).some(Boolean);

  const handleSubmit = () => {
    setAttempted(true);
    if (!isValid) return;
    sendMessage.mutate({
      name: name.trim(),
      email: email.trim(),
      subject: subject.trim(),
      message: message.trim(),
      website,
    });
  };

  if (sendMessage.isSuccess) {
    return (
      <div className="section-pad">
        <div className="mx-auto max-w-[480px] border border-accent p-8 text-center">
          <p className="text-[20px] font-bold text-ink">
            Дякуємо! Ми відповімо на {email.trim()} найближчим часом.
          </p>
        </div>
      </div>
    );
  }

  const fieldClass = (hasError: boolean) => (attempted && hasError ? 'border-warn' : '');

  return (
    <div className="section-pad">
      <h1 className="m-0 mb-3 text-[clamp(32px,6vw,72px)] font-extrabold tracking-[-0.045em]">
        Зворотній зв’язок
      </h1>
      <p className="mb-8 text-[15px] text-muted">
        Маєте питання, пропозицію чи знайшли помилку? Напишіть нам.
      </p>

      <div className="max-w-[520px] space-y-3">
        <div>
          <Input
            placeholder="Імʼя"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={100}
            className={fieldClass(errors.name)}
          />
          {attempted && errors.name && <p className="mt-1 text-[12px] text-warn">Вкажіть імʼя</p>}
        </div>

        <div>
          <Input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            maxLength={254}
            className={fieldClass(errors.email)}
          />
          {attempted && errors.email && (
            <p className="mt-1 text-[12px] text-warn">Вкажіть коректний email</p>
          )}
        </div>

        <div>
          <Input
            placeholder="Тема"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            maxLength={150}
            className={fieldClass(errors.subject)}
          />
          {attempted && errors.subject && (
            <p className="mt-1 text-[12px] text-warn">Вкажіть тему</p>
          )}
        </div>

        <div>
          <Textarea
            placeholder="Повідомлення"
            rows={6}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={5000}
            className={fieldClass(errors.message)}
          />
          {attempted && errors.message && (
            <p className="mt-1 text-[12px] text-warn">Напишіть повідомлення</p>
          )}
        </div>

        {/* Пастка: за межами екрана, а не display:none — дехто з ботів пропускає невидимі поля. */}
        <input
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          className="absolute left-[-10000px] h-px w-px overflow-hidden"
        />

        {sendMessage.isError && (
          <p className="text-[13px] text-warn">{sendMessage.error.message}</p>
        )}

        <Button
          variant="accent"
          size="lg"
          className="w-full"
          disabled={sendMessage.isPending}
          onClick={handleSubmit}
        >
          НАДІСЛАТИ
        </Button>

        <p className="text-[12px] text-muted-2">
          Надсилаючи форму, ви погоджуєтесь з{' '}
          <Link to="/privacy" className="text-muted underline">
            Політикою конфіденційності
          </Link>
          .
        </p>

        <p className="text-[13px] text-muted">
          Або напишіть напряму:{' '}
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-accent no-underline">
            {CONTACT_EMAIL}
          </a>
        </p>
      </div>
    </div>
  );
}
